<template>
  <div class="dashboard-page px-3 sm:px-5 pt-3 pb-2 mb-5">
    <div class="dashboard-header mb-4">
      <div v-if="appliedFilterChips.length" class="applied-filters">
        <span class="applied-filters-heading">Applied Filters:</span>
        <div
          v-for="chip in appliedFilterChips"
          :key="chip.key"
          class="applied-filter-chip"
        >
          <span class="applied-filter-label">{{ chip.label }}:</span>
          <span class="applied-filter-value">{{ chip.value }}</span>
          <button
            type="button"
            class="applied-filter-remove"
            :aria-label="'Remove ' + chip.label + ' filter'"
            @click="removeAppliedFilter(chip.key)"
          >
            <img src="@/assets/cancel.svg" alt="" />
          </button>
        </div>
      </div>
      <button type="button" class="flex filterButton dashboard-filter-button" @click="filterOpen">
        <img src="@/assets/filterIcon.svg" class="filterIcon" alt="" />
        Filters
      </button>
    </div>

    <div class="kpi-grid mb-4">
      <router-link
        v-for="kpi in kpiCards"
        :key="kpi.key"
        class="kpi-card text-left"
        :class="'kpi-card--' + kpi.tone"
        :to="kpiRoute(kpi.key)"
        @click="markTasksRoute"
      >
        <div class="kpi-label">{{ kpi.label }}</div>
        <div class="kpi-value">{{ kpi.display }}</div>
      </router-link>
    </div>

    <loader v-if="dataLoading" />

    <template v-else>
      <div class="chart-grid mb-4">
        <div class="card p-3 chart-card">
          <div class="card-title">Task Status</div>
          <div v-if="hasStatusData" class="chart-body">
            <apexchart
              :key="'status-' + chartKey"
              type="donut"
              height="280"
              width="100%"
              :options="statusChartOptions"
              :series="statusSeries"
            />
          </div>
          <div v-else class="no-data">No data for selected filters</div>
        </div>
        <div class="card p-3 chart-card">
          <div class="card-title">Tasks by Team</div>
          <div v-if="hasTeamData" class="chart-body" :key="teamChartRenderKey">
            <apexchart
              type="bar"
              height="280"
              width="100%"
              :options="departmentChartOptions"
              :series="departmentSeries"
            />
          </div>
          <div v-else class="no-data">No data for selected filters</div>
        </div>
        <div class="card p-3 chart-card">
          <div class="card-title">Tasks by Framework</div>
          <div v-if="hasFrameworkData" class="chart-body">
            <apexchart
              :key="'framework-' + chartKey"
              type="bar"
              height="280"
              width="100%"
              :options="frameworkChartOptions"
              :series="frameworkSeries"
            />
          </div>
          <div v-else class="no-data">No data for selected filters</div>
        </div>
        <div class="card p-3 chart-card">
          <div class="card-title">Overdue Aging (in days)</div>
          <div v-if="hasAgingData" class="chart-body">
            <apexchart
              :key="'aging-' + chartKey"
              type="bar"
              height="280"
              width="100%"
              :options="agingChartOptions"
              :series="agingSeries"
            />
          </div>
          <div v-else class="no-data">No overdue tasks</div>
        </div>
      </div>

      <div class="card p-4 chart-card chart-card-wide mb-4">
        <div class="card-title">Task Execution Trend</div>
        <div v-if="hasTrendData" class="chart-body chart-body-wide">
          <apexchart
            :key="'trend-' + chartKey"
            type="line"
            height="320"
            width="100%"
            :options="trendChartOptions"
            :series="trendSeries"
          />
        </div>
        <div v-else class="no-data">No data for selected filters</div>
      </div>
    </template>

    <popup v-if="showFilters" :popUp="showFilters">
      <div class="overflow-x-hidden overflow-y-auto fixed inset-0 z-index justify-end items-start flex">
        <div class="relative w-full max-w-full sm:max-w-xl">
          <div class="border-0 rounded relative flex flex-col w-full bg-white h-screen">
            <div class="flex items-center justify-between popupheadingcontainer rounded">
              <h1>Filters</h1>
              <button type="button" class="float-right text-3xl" @click="onCancelFilters">
                <img src="@/assets/cancel.svg" class="h-5 w-5 cursor-pointer" alt="" />
              </button>
            </div>
            <div class="border-b border-solid border-lightgrey ml-4 mr-4"></div>
            <div class="impactcontainer popupbodycontainer flex-1 overflow-y-auto">
              <div class="formcontainer flex flex-col px-4 pb-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  <div>
                    <label class="controllabel" for="dashboard-time-period">Time Period</label>
                    <Select
                      id="dashboard-time-period"
                      inputId="dashboard-time-period"
                      v-model="filters.timePeriod"
                      :options="timePeriodOptions"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select"
                      showClear
                      appendTo="self"
                      class="w-full mt-1"
                      @change="onTimePeriodChange"
                    />
                  </div>
                  <div v-if="isCustomTimePeriod">
                    <label class="controllabel" for="dashboard-from-date">From Date</label>
                    <input
                      id="dashboard-from-date"
                      type="date"
                      v-model="filters.fromDate"
                      class="inputboxstyling mt-1 w-full"
                      :max="filters.toDate || maxDate"
                      @focus="openCalendar"
                      @keydown.prevent
                    />
                  </div>
                  <div v-if="isCustomTimePeriod">
                    <label class="controllabel" for="dashboard-to-date">To Date</label>
                    <input
                      id="dashboard-to-date"
                      type="date"
                      v-model="filters.toDate"
                      class="inputboxstyling mt-1 w-full"
                      :min="filters.fromDate || undefined"
                      :max="maxDate"
                      @focus="openCalendar"
                      @keydown.prevent
                    />
                  </div>
                  <div>
                    <label class="controllabel" for="dashboard-task-status">Task Status</label>
                    <MultiSelect
                      inputId="dashboard-task-status"
                      v-model="filters.taskStatus"
                      :options="statusOptions"
                      optionLabel="label"
                      dataKey="id"
                      placeholder="Select"
                      display="chip"
                      :maxSelectedLabels="2"
                      appendTo="self"
                      class="w-full mt-1"
                    />
                  </div>
                  <div>
                    <label class="controllabel" for="dashboard-framework">Framework</label>
                    <MultiSelect
                      inputId="dashboard-framework"
                      v-model="filters.framework"
                      :options="frameworkOptions"
                      optionLabel="label"
                      dataKey="value"
                      placeholder="Select"
                      display="chip"
                      :filter="true"
                      :maxSelectedLabels="2"
                      appendTo="self"
                      class="w-full mt-1"
                    />
                  </div>
                  <div>
                    <label class="controllabel" for="dashboard-teams">Teams</label>
                    <MultiSelect
                      inputId="dashboard-teams"
                      v-model="filters.departments"
                      :options="departmentsList"
                      optionLabel="label"
                      dataKey="value"
                      placeholder="Select"
                      display="chip"
                      :filter="true"
                      :maxSelectedLabels="2"
                      appendTo="self"
                      class="w-full mt-1"
                    />
                  </div>
                  <div>
                    <label class="controllabel" for="dashboard-locations">Location</label>
                    <MultiSelect
                      inputId="dashboard-locations"
                      v-model="filters.locations"
                      :options="locations"
                      optionLabel="label"
                      dataKey="value"
                      placeholder="Select"
                      display="chip"
                      :filter="true"
                      :maxSelectedLabels="2"
                      appendTo="self"
                      class="w-full mt-1"
                    />
                  </div>
                  <div>
                    <label class="controllabel" for="dashboard-frequency">Frequency</label>
                    <MultiSelect
                      inputId="dashboard-frequency"
                      v-model="filters.controlFrequency"
                      :options="dbFrequency"
                      optionLabel="key"
                      dataKey="value"
                      placeholder="Select"
                      display="chip"
                      :maxSelectedLabels="2"
                      appendTo="self"
                      class="w-full mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="flex justify-end items-center pb-4 pl-5 pt-4 pr-5 border-t border-lightgrey">
              <button type="button" class="filterButton mr-2" @click="applyResetFilters">Reset</button>
              <button type="button" class="btnprimary" @click="applyFilters">Apply</button>
            </div>
          </div>
        </div>
      </div>
    </popup>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { toast } from "../main";
import VueApexCharts from "vue3-apexcharts";
import Select from "primevue/select";
import MultiSelect from "primevue/multiselect";
import loader from "@/components/loader.vue";
import popup from "@/components/popup.vue";

const CUSTOM_TIME_PERIOD = "custom";

const emptyFilters = () => ({
  timePeriod: null,
  fromDate: "",
  toDate: "",
  taskStatus: [],
  locations: [],
  departments: [],
  controlFrequency: [],
  framework: [],
});

const STATUS_COLORS: Record<string, string> = {
  Open: "#1976D2",
  "In Progress": "#FF9900",
  InProgress: "#FF9900",
  "In Review": "#8600F0",
  Completed: "#00B050",
  Failed: "#C00000",
  Draft: "#9CA3AF",
  Terminated: "#64748B",
};

const KPI_SERIES: Record<string, string> = {
  all: "planner_allTasks",
  template: "Template_Tasks",
  sub: "Sub_Tasks",
  custom: "Custom_Tasks",
  recurring: "Recurring_Tasks",
};

const ARRAY_FILTER_FIELDS = [
  { filterKey: "taskStatus", payloadKey: "taskStatus", valueKey: "id" },
  { filterKey: "locations", payloadKey: "locations", valueKey: "value" },
  { filterKey: "departments", payloadKey: "departments", valueKey: "value" },
  { filterKey: "controlFrequency", payloadKey: "controlFrequency", valueKey: "value" },
  { filterKey: "framework", payloadKey: "framework", valueKey: "label" },
];

const serializeFilters = (filters: any) => {
  const payload: any = {};
  if (filters.timePeriod && filters.timePeriod !== CUSTOM_TIME_PERIOD) {
    payload.timePeriod = Number(filters.timePeriod);
  }
  if (filters.timePeriod === CUSTOM_TIME_PERIOD) {
    if (filters.fromDate) {
      payload.fromDate = filters.fromDate;
    }
    if (filters.toDate) {
      payload.toDate = filters.toDate;
    }
  }
  ARRAY_FILTER_FIELDS.forEach(({ filterKey, payloadKey, valueKey }) => {
    const items = filters[filterKey];
    if (Array.isArray(items) && items.length) {
      payload[payloadKey] = items.map((item: any) => {
        const value = item?.[valueKey] ?? item;
        return payloadKey === "controlFrequency" || payloadKey === "taskStatus" ? Number(value) : value;
      });
    }
  });
  return payload;
};

export default defineComponent({
  components: {
    apexchart: VueApexCharts,
    Select,
    MultiSelect,
    loader,
    popup,
  },
  data(): any {
    return {
      dataLoading: false,
      chartKey: 0,
      activeKpi: "all",
      showFilters: false,
      filterSnapshot: null,
      filters: emptyFilters(),
      appliedFilters: emptyFilters(),
      dashboardData: {},
      locations: [],
      departmentsList: [],
      frameworkOptions: [],
      statusSeries: [],
      agingSeries: [],
      trendSeries: [],
      departmentSeries: [],
      teamChartRenderKey: 0,
      frameworkSeries: [],
      statusChartOptions: {},
      agingChartOptions: {},
      trendChartOptions: {},
      departmentChartOptions: {},
      frameworkChartOptions: {},
      timePeriodOptions: [
        { label: "This Quarter", value: 20603 },
        { label: "Last Quarter", value: 20604 },
        { label: "This Year", value: 20601 },
        { label: "Last Year", value: 20602 },
        { label: "Custom", value: CUSTOM_TIME_PERIOD },
      ],
      statusOptions: [
        { id: 10411, label: "Draft" },
        { id: 10401, label: "Open" },
        { id: 10402, label: "In Progress" },
        { id: 10415, label: "In Review" },
        { id: 10403, label: "Completed" },
        { id: 10420, label: "Failed" },
        { id: 10418, label: "Terminated" },
      ],
    };
  },
  computed: {
    dbFrequency(): any {
      return this.$store.getters.dbFrequency;
    },
    isCustomTimePeriod(): boolean {
      return this.filters.timePeriod === CUSTOM_TIME_PERIOD;
    },
    appliedFilterChips(): { key: string; label: string; value: string }[] {
      const filters = this.appliedFilters || emptyFilters();
      const chips: { key: string; label: string; value: string }[] = [];
      const joinLabels = (items: any[], labelKey = "label") =>
        (items || []).map((item: any) => item?.[labelKey]).filter(Boolean).join(", ");

      if (filters.timePeriod) {
        const period = this.timePeriodOptions.find((option: any) => option.value === filters.timePeriod);
        let value = period?.label || String(filters.timePeriod);
        if (filters.timePeriod === CUSTOM_TIME_PERIOD) {
          const from = this.formatChipDate(filters.fromDate);
          const to = this.formatChipDate(filters.toDate);
          value = from && to ? `Custom (${from} to ${to})` : "Custom";
        }
        chips.push({ key: "timePeriod", label: "Time Period", value });
      }
      if (filters.taskStatus?.length) {
        chips.push({ key: "taskStatus", label: "Task Status", value: joinLabels(filters.taskStatus) });
      }
      if (filters.framework?.length) {
        chips.push({ key: "framework", label: "Framework", value: joinLabels(filters.framework) });
      }
      if (filters.departments?.length) {
        chips.push({ key: "departments", label: "Teams", value: joinLabels(filters.departments) });
      }
      if (filters.locations?.length) {
        chips.push({ key: "locations", label: "Location", value: joinLabels(filters.locations) });
      }
      if (filters.controlFrequency?.length) {
        chips.push({ key: "controlFrequency", label: "Frequency", value: joinLabels(filters.controlFrequency, "key") });
      }
      return chips;
    },
    maxDate(): string {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    kpiCards(): { key: string; label: string; display: string; tone: string }[] {
      const kpi = this.dashboardData?.KPI || {};
      return [
        { key: "active", label: "Total Active Tasks", display: String(kpi.Total_Active_Tasks || 0), tone: "neutral" },
        { key: "dueSoon", label: "Due Soon", display: String(kpi.Due_Soon || 0), tone: "attention" },
        { key: "overdue", label: "Overdue", display: String(kpi.Overdue || 0), tone: "risk" },
        { key: "completedPeriod", label: "Completed This Period", display: String(kpi.Completed_This_Period || 0), tone: "positive" },
        { key: "onTime", label: "On-Time Completion Rate", display: `${kpi.On_Time_Completion_Rate || 0}%`, tone: "positive" },
      ];
    },
    statusChartTitle(): string {
      return "Task Status";
    },
    hasStatusData(): boolean {
      return Array.isArray(this.statusSeries) && this.statusSeries.length > 0;
    },
    hasAgingData(): boolean {
      return Array.isArray(this.agingSeries) && this.agingSeries[0]?.data?.some((value: number) => value > 0);
    },
    hasTrendData(): boolean {
      return Array.isArray(this.trendSeries) && this.trendSeries.some((series: any) => series.data?.some((value: number) => value > 0));
    },
    hasTeamData(): boolean {
      return Array.isArray(this.departmentSeries) && this.departmentSeries.some((series: any) =>
        series.data?.some((value: number) => value > 0)
      );
    },
    hasFrameworkData(): boolean {
      return Array.isArray(this.frameworkSeries) && this.frameworkSeries[0]?.data?.length > 0;
    },
  },
  async mounted() {
    await Promise.all([
      this.getAllEntities(),
      this.fetchDepartmentsList(),
      this.getAllFrameworks(),
    ]);
    await this.fetchDashboardData();
  },
  methods: {
    openCalendar(event: any) {
      if (event?.target?.showPicker) {
        event.target.showPicker();
      }
    },
    baseChartOptions() {
      return {
        chart: {
          toolbar: { show: false },
          fontFamily: "Roboto",
          width: "100%",
          parentHeightOffset: 0,
          redrawOnWindowResize: true,
          redrawOnParentResize: true,
        },
        legend: {
          position: "bottom",
          fontSize: "12px",
        },
        dataLabels: { enabled: true },
        noData: { text: "No data for selected filters" },
      };
    },
    barHeightForCategories(count: number) {
      const categories = Math.max(count, 1);
      const plotHeight = 210;
      const maxBarPx = 28;
      const percent = (maxBarPx * categories * 100) / plotHeight;
      return `${Math.round(Math.min(70, Math.max(14, percent)))}%`;
    },
    stackedBarPlotOptions(categoryCount: number) {
      return {
        bar: {
          horizontal: true,
          borderRadius: 2,
          barHeight: this.barHeightForCategories(categoryCount),
          dataLabels: {
            total: {
              enabled: true,
              style: {
                fontSize: "12px",
                fontWeight: 600,
              },
            },
          },
        },
      };
    },
    selectedFilterIds(items: any[], valueKey = "value") {
      return new Set(
        (items || [])
          .map((item: any) => String(item?.[valueKey] ?? item?.id ?? item ?? ""))
          .filter(Boolean)
      );
    },
    selectedStatusIds(items: any[]) {
      const ids = new Set<number>();
      (items || []).forEach((item: any) => {
        const id = Number(item?.id ?? item);
        if (Number.isNaN(id)) {
          return;
        }
        ids.add(id);
        if (id === 10414 || id === 10415) {
          ids.add(10414);
          ids.add(10415);
        }
      });
      return ids;
    },
    onTimePeriodChange() {
      if (this.filters.timePeriod !== CUSTOM_TIME_PERIOD) {
        this.filters.fromDate = "";
        this.filters.toDate = "";
      }
    },
    formatChipDate(value: string) {
      if (!value) {
        return "";
      }
      const parts = String(value).split("-");
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return value;
    },
    syncAppliedFilters() {
      this.appliedFilters = JSON.parse(JSON.stringify(this.filters));
    },
    removeAppliedFilter(key: string) {
      if (key === "timePeriod") {
        this.filters.timePeriod = null;
        this.filters.fromDate = "";
        this.filters.toDate = "";
      } else {
        this.filters[key] = [];
      }
      this.syncAppliedFilters();
      this.fetchDashboardData();
    },
    kpiQuery(key: string) {
      const query: Record<string, string> = { kpi: key };
      if (this.filters.timePeriod && this.filters.timePeriod !== CUSTOM_TIME_PERIOD) {
        query.timePeriod = String(this.filters.timePeriod);
      }
      if (this.isCustomTimePeriod) {
        if (this.filters.fromDate) {
          query.fromDate = this.filters.fromDate;
        }
        if (this.filters.toDate) {
          query.toDate = this.filters.toDate;
        }
      }
      return query;
    },
    kpiRoute(key: string) {
      return { name: "tasks", path: "/planner/tasks", query: this.kpiQuery(key) };
    },
    markTasksRoute() {
      this.$store.dispatch("selectedRoute", "tasks");
    },
    filterOpen() {
      this.filterSnapshot = JSON.parse(JSON.stringify(this.filters));
      this.showFilters = true;
    },
    onCancelFilters() {
      if (this.filterSnapshot) {
        this.filters = this.filterSnapshot;
      }
      this.showFilters = false;
    },
    applyFilters() {
      if (this.isCustomTimePeriod && (!this.filters.fromDate || !this.filters.toDate)) {
        toast.error("Please select From Date and To Date", {
          timeout: 1000,
          closeOnClick: true,
          closeButton: "button",
          icon: true,
        });
        return;
      }
      this.showFilters = false;
      this.syncAppliedFilters();
      this.fetchDashboardData();
    },
    applyResetFilters() {
      this.filters = emptyFilters();
      this.activeKpi = "all";
      this.showFilters = false;
      this.syncAppliedFilters();
      this.fetchDashboardData();
    },
    async getAllEntities() {
      try {
        const res = await this.$http.get(`${process.env.VUE_APP_ORG_API}/api/org/businessEntities/getall`);
        this.locations = (res.data || []).map((obj: any) => ({
          value: obj._id,
          label: obj.entityDisplayName,
        }));
      } catch (error) {
        toast.error(`Entities fetch failed: ${error}`);
      }
    },
    async fetchDepartmentsList() {
      try {
        const res = await this.$http.get(`${process.env.VUE_APP_ORG_API}/departments/get`);
        this.departmentsList = (res.data || []).map((obj: any) => ({
          value: obj._id,
          label: obj.displayName,
        }));
      } catch (error) {
        toast.error(`Departments fetch failed: ${error}`);
      }
    },
    frameworkName(entry: any) {
      if (!entry) {
        return "";
      }
      if (typeof entry === "string") {
        return entry;
      }
      return entry.name || entry.framework || entry.frameWorkName || "";
    },
    async getAllFrameworks() {
      try {
        const result = await this.$http.get(
          `${process.env.VUE_APP_CONTROLS_API}/api/org/controls/active?frameworks=true`
        );
        const names = new Set<string>();
        (result.data || []).forEach((control: any) => {
          const topLevel = control.frameWorkName || control.frameworkName;
          if (topLevel) {
            names.add(topLevel);
          }
          if (Array.isArray(control.frameworks)) {
            control.frameworks.forEach((framework: any) => {
              const name = this.frameworkName(framework);
              if (name) {
                names.add(name);
              }
            });
          }
        });
        this.frameworkOptions = Array.from(names)
          .filter((name) => name !== "InternalControls" && name !== "Internal Controls")
          .sort()
          .map((name) => ({ label: name, value: name }));
      } catch (error) {
        toast.error(`Frameworks fetch failed: ${error}`);
      }
    },
    updateStatusChart() {
      const sourceKey = KPI_SERIES[this.activeKpi] || KPI_SERIES.all;
      const items = (this.dashboardData[sourceKey] || []).filter((item: any) => item.total > 0);
      this.statusSeries = items.map((item: any) => item.total);
      this.statusChartOptions = {
        ...this.baseChartOptions(),
        labels: items.map((item: any) => item.label),
        colors: items.map((item: any) => STATUS_COLORS[item.label] || "#1976D2"),
        dataLabels: { enabled: false },
        tooltip: {
          enabled: true,
          followCursor: true,
          fillSeriesColor: false,
          theme: "light",
        },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "12px",
                  fontWeight: 400,
                  color: "#6b7280",
                  offsetY: 18,
                },
                value: {
                  show: true,
                  fontSize: "22px",
                  fontWeight: 600,
                  color: "#111827",
                  offsetY: -8,
                },
                total: {
                  show: true,
                  showAlways: true,
                  label: "Total",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#6b7280",
                  formatter: (w: any) =>
                    (w.globals.seriesTotals || []).reduce((sum: number, value: number) => sum + value, 0),
                },
              },
            },
          },
        },
      };
    },
    updateAgingChart() {
      const items = this.dashboardData.Overdue_Aging || [];
      this.agingChartOptions = {
        ...this.baseChartOptions(),
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "45%",
          },
        },
        xaxis: {
          categories: items.map((item: any) => String(item.label || "").replace(/\s*days?/i, "").trim()),
          labels: {
            rotate: -45,
            hideOverlappingLabels: true,
            trim: true,
            style: { fontSize: "11px" },
          },
        },
        colors: ["#C00000"],
        dataLabels: { enabled: true },
      };
      this.agingSeries = [
        {
          name: "Overdue Tasks",
          data: items.map((item: any) => item.total || 0),
        },
      ];
    },
    updateTrendChart() {
      const items = this.dashboardData.Execution_Trend || [];
      this.trendChartOptions = {
        ...this.baseChartOptions(),
        stroke: {
          curve: "smooth",
          width: 3,
        },
        xaxis: {
          categories: items.map((item: any) => item.label),
        },
        colors: ["#C00000", "#00B050"],
        dataLabels: { enabled: false },
        legend: {
          position: "bottom",
          fontSize: "12px",
        },
      };
      this.trendSeries = [
        {
          name: "Tasks Due",
          data: items.map((item: any) => item.due || 0),
        },
        {
          name: "Tasks Completed",
          data: items.map((item: any) => item.completed || 0),
        },
      ];
    },
    updateFrameworkChart() {
      const items = (this.dashboardData.Framework_Tasks || []).filter((item: any) => item.total > 0);
      const statusOrder = ["Open", "In Progress", "In Review", "Completed", "Failed", "Draft", "Terminated"];
      const statusIds: Record<string, number[]> = {
        Open: [10401],
        "In Progress": [10402],
        "In Review": [10414, 10415],
        Completed: [10403],
        Failed: [10420],
        Draft: [10411],
        Terminated: [10418],
      };
      const statusAliases = (label: string) => {
        if (label === "In Progress") return ["In Progress", "InProgress"];
        if (label === "Terminated") return ["Terminated"];
        return [label];
      };
      const frameworkStatusTotal = (framework: any, label: string) => {
        const aliases = statusAliases(label);
        const match = (framework.status || []).find(
          (item: any) =>
            aliases.includes(item.label) || (statusIds[label] || []).includes(Number(item.id ?? item.status))
        );
        return match?.total || 0;
      };
      const series = statusOrder
        .map((label) => ({
          name: label,
          data: items.map((framework: any) => frameworkStatusTotal(framework, label)),
        }))
        .filter((item) => item.data.some((value: number) => value > 0));
      this.frameworkChartOptions = {
        ...this.baseChartOptions(),
        chart: {
          ...this.baseChartOptions().chart,
          stacked: true,
          stackType: "normal",
        },
        plotOptions: this.stackedBarPlotOptions(items.length),
        xaxis: {
          categories: items.map((item: any) => item.label),
        },
        yaxis: {
          labels: {
            maxWidth: 140,
            trim: true,
            style: { fontSize: "11px" },
          },
        },
        colors: series.map((item) => STATUS_COLORS[item.name] || "#1976D2"),
        dataLabels: {
          enabled: true,
          formatter: (value: number) => (value > 0 ? value : ""),
        },
        legend: {
          position: "bottom",
          fontSize: "10px",
          itemMargin: { horizontal: 4, vertical: 0 },
        },
      };
      this.frameworkSeries = series;
    },
    updateTeamChart() {
      const filters = this.appliedFilters || emptyFilters();
      const selectedLocations = this.selectedFilterIds(filters.locations);
      const selectedTeams = this.selectedFilterIds(filters.departments);
      const selectedStatuses = this.selectedStatusIds(filters.taskStatus);
      const locations = (this.dashboardData.Team_Tasks || []).filter(Boolean);
      const teams = locations
        .filter((location: any) =>
          !selectedLocations.size || selectedLocations.has(String(location.locationId))
        )
        .flatMap((location: any) =>
          (location.teams || []).map((team: any) => ({
            ...team,
            locationName: location.locationName,
            locationId: location.locationId,
          }))
        )
        .filter((team: any) => !selectedTeams.size || selectedTeams.has(String(team.teamId)))
        .map((team: any) => {
          const status = (team.status || []).filter((item: any) => {
            if (!selectedStatuses.size) {
              return true;
            }
            return selectedStatuses.has(Number(item.id ?? item.status ?? item.statusCode));
          });
          const totalTasks = status.reduce((sum: number, item: any) => sum + (Number(item.total) || 0), 0);
          return { ...team, status, totalTasks };
        })
        .filter((team: any) => team.totalTasks > 0);
      const locationCount = new Set(teams.map((team: any) => team.locationName)).size;
      const teamLabels = teams.map((team: any) =>
        locationCount > 1 ? `${team.teamName} (${team.locationName})` : team.teamName
      );
      const statusOrder = ["Open", "In Progress", "In Review", "Completed", "Failed", "Draft", "Terminated"];
      const statusIds: Record<string, number[]> = {
        Open: [10401],
        "In Progress": [10402],
        "In Review": [10414, 10415],
        Completed: [10403],
        Failed: [10420],
        Draft: [10411],
        Terminated: [10418],
      };
      const statusAliases = (label: string) => {
        if (label === "In Progress") return ["In Progress", "InProgress"];
        if (label === "Terminated") return ["Terminated", "Terminate"];
        return [label];
      };
      const teamStatusTotal = (team: any, label: string) => {
        const aliases = statusAliases(label);
        const ids = statusIds[label] || [];
        return (team.status || []).reduce((sum: number, item: any) => {
          const code = Number(item.id ?? item.status ?? item.statusCode);
          const matches = aliases.includes(item.label) || ids.includes(code);
          return matches ? sum + (Number(item.total) || 0) : sum;
        }, 0);
      };
      const series = statusOrder
        .map((label) => ({
          name: label,
          data: teams.map((team: any) => teamStatusTotal(team, label)),
        }))
        .filter((item) => item.data.some((value: number) => value > 0));
      this.departmentChartOptions = {
        ...this.baseChartOptions(),
        chart: {
          ...this.baseChartOptions().chart,
          id: `team-tasks-${Date.now()}`,
          stacked: true,
          stackType: "normal",
          animations: { enabled: false },
        },
        plotOptions: this.stackedBarPlotOptions(teams.length),
        xaxis: {
          categories: teamLabels,
        },
        yaxis: {
          categories: teamLabels,
          labels: {
            maxWidth: 140,
            trim: true,
            style: { fontSize: "11px" },
          },
        },
        colors: series.map((item) => STATUS_COLORS[item.name] || "#1976D2"),
        dataLabels: {
          enabled: true,
          formatter: (value: number) => (value > 0 ? value : ""),
        },
        legend: {
          position: "bottom",
          fontSize: "10px",
          itemMargin: { horizontal: 4, vertical: 0 },
        },
      };
      this.departmentSeries = series;
      this.teamChartRenderKey = Date.now();
    },
    applyChartData() {
      this.updateStatusChart();
      this.updateAgingChart();
      this.updateTrendChart();
      this.updateTeamChart();
      this.updateFrameworkChart();
      this.chartKey += 1;
    },
    async fetchDashboardData() {
      if (this.filters.fromDate && this.filters.toDate && this.filters.fromDate > this.filters.toDate) {
        toast.error("Start Date Should not be greater End Date", {
          timeout: 1000,
          closeOnClick: true,
          closeButton: "button",
          icon: true,
        });
        return;
      }

      if (!process.env.VUE_APP_ANALYTICS_API) {
        toast.error("Analytics API URL is not configured");
        return;
      }

      this.dataLoading = true;
      this.activeKpi = "all";
      try {
        const res = await this.$http.post(`${process.env.VUE_APP_ANALYTICS_API}/planner/dashboard`, {
          filters: serializeFilters(this.filters),
        });
        const payload = res?.data?.Team_Tasks || res?.data?.KPI || res?.data?.planner_allTasks
          ? res.data
          : res?.data?.data && Object.keys(res.data.data).length
            ? res.data.data
            : {};
        this.dashboardData = payload;
        this.applyChartData();
        await this.$nextTick();
      } catch (error) {
        this.dashboardData = {};
        this.applyChartData();
        toast.error(`Tasks fetch failed: ${error}`);
      } finally {
        this.dataLoading = false;
      }
    },
  },
});
</script>

<style scoped>
.dashboard-page {
  --kpi-min-width: 11.5rem;
  --kpi-min-height: 5.5rem;
  --chart-min-width: 18rem;
  --chart-min-height: 22rem;
  --chart-plot-height: 17.5rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.dashboard-header {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 0.75rem;
}
.dashboard-filter-button {
  flex-shrink: 0;
  margin-left: auto;
}
.applied-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  flex: 1 1 0;
  min-width: 0;
}
.applied-filters-heading {
  font-size: 0.8rem;
  font-weight: 600;
  color: #4d4d4d;
  white-space: nowrap;
}
.applied-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  max-width: 100%;
  padding: 0.25rem 0.4rem 0.25rem 0.7rem;
  border: 1px solid #e9e9e9;
  border-radius: 4px;
  background: #f9f9fb;
  font-size: 0.8rem;
  line-height: 1.3;
}
.applied-filter-value {
  color: #1976d2;
  overflow-wrap: anywhere;
}
.applied-filter-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.1rem;
  height: 1.1rem;
  padding: 0;
  margin-left: 0.15rem;
  border: none;
  background: transparent;
  cursor: pointer;
}
.applied-filter-remove img {
  width: 0.7rem;
  height: 0.7rem;
}
.kpi-grid,
.chart-grid {
  display: grid;
  gap: 0.75rem;
  width: 100%;
}
.kpi-grid {
  grid-template-columns: repeat(auto-fit, minmax(min(100%, var(--kpi-min-width)), 1fr));
}
.chart-grid {
  grid-template-columns: repeat(auto-fit, minmax(min(100%, var(--chart-min-width)), 1fr));
  align-items: stretch;
}
.kpi-card {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-decoration: none;
  color: inherit;
  min-width: 0;
  height: auto !important;
  min-height: var(--kpi-min-height);
  background: #ffffff;
  border: 1px solid #e9e9e9;
  border-radius: 8px;
  padding: 0.9rem 1rem 0.9rem 0.95rem;
  transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
}
.kpi-card--neutral {
  border-left: 5px solid #1976d2;
  background: #f8fbff;
}
.kpi-card--muted {
  border-left: 5px solid #9ca3af;
  background: #f4f5f7;
}
.kpi-card--positive {
  border-left: 5px solid #00b050;
  background: #f3fbf6;
}
.kpi-card--attention {
  border-left: 5px solid #ff9900;
  background: #fff8f0;
}
.kpi-card--risk {
  border-left: 5px solid #c00000;
  background: #fdf5f5;
}
.kpi-card:hover {
  box-shadow: 0 6px 16px rgba(17, 24, 39, 0.08);
  transform: translateY(-1px);
  border-color: #d4d4d8;
}
.kpi-card:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}
.kpi-label {
  color: #4d4d4d;
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 0.35rem;
  overflow-wrap: anywhere;
}
.kpi-value {
  color: #111827;
  font-size: 1.5rem;
  font-weight: 650;
  line-height: 1.2;
}
.applied-filter-label {
  color: #4d4d4d;
  font-weight: 600;
}
.chart-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: var(--chart-min-height);
  overflow: visible;
  position: relative;
}
.chart-card:hover {
  z-index: 3;
}
.chart-card-wide {
  min-height: 24rem;
}
.chart-body {
  flex: 1 1 auto;
  width: 100%;
  min-height: var(--chart-plot-height);
  overflow: visible;
  position: relative;
}
.chart-body-wide {
  min-height: 20rem;
}
.chart-body :deep(.apexcharts-canvas) {
  max-width: 100%;
  overflow: visible;
}
.chart-body :deep(.apexcharts-tooltip) {
  z-index: 20;
}
.card-title {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}
.no-data {
  text-align: center;
  color: #9ca3af;
  font-size: 0.9rem;
  padding: 4rem 1rem;
}
</style>
